import * as Blockly from 'blockly/core';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import { useBlockModalVariableStore } from '@/stores/useBlockModalVariableStore';
import { darken } from '@/(routers)/boards/editor/block/blockTheme';

const FIELD_HEIGHT = 42;
const FIELD_MIN_WIDTH = 115;
const PADDING_H = 12;

const LOCK_SIZE = 18;
const LOCK_LEFT = 10;
const LOCK_MARGIN_RIGHT = 10;

const SYMBOL_SIZE = 9;
const SYMBOL_MARGIN_RIGHT = 6;

const SYMBOL_LEFT_NO_LOCK = LOCK_LEFT;
const SYMBOL_LEFT_WITH_LOCK = LOCK_LEFT + LOCK_SIZE + LOCK_MARGIN_RIGHT;

const TEXT_X_NO_SYMBOL = PADDING_H;
const TEXT_X_WITH_SYMBOL = SYMBOL_LEFT_NO_LOCK + SYMBOL_SIZE + SYMBOL_MARGIN_RIGHT;
const TEXT_X_WITH_LOCK_AND_SYMBOL = SYMBOL_LEFT_WITH_LOCK + SYMBOL_SIZE + SYMBOL_MARGIN_RIGHT;

const SVG_STYLE = 'pointer-events: none; transform: none !important;';

export class FieldVariableLabel extends Blockly.Field<string> {
    private labelEl_: SVGTextElement | null = null;
    private symbolEl_: SVGElement | null = null;
    private lockEl_: SVGElement | null = null;
    private fontWeight_: string;
    private varType_: 'float' | 'String' | 'bool' | null;
    private colorOverride_: string | null = null;
    private isPrivate_: boolean;
    private showSymbol_: boolean;
    private onSet_: ((name: string, field: FieldVariableLabel) => boolean) | null;

    static override fromJson(options: Blockly.FieldConfig & { varName?: string }): FieldVariableLabel {
        return new FieldVariableLabel(options.varName ?? '設定する変数');
    }

    constructor(
        varName: string,
        options?: {
            fontWeight?: string;
            varType?: 'float' | 'String' | 'bool';
            colorOverride?: string;
            isPrivate?: boolean;
            showSymbol?: boolean;
            onSet?: (name: string, field: FieldVariableLabel) => boolean;
        },
    ) {
        super(varName);
        this.fontWeight_ = options?.fontWeight ?? 'normal';
        this.varType_ = options?.varType ?? null;
        this.colorOverride_ = options?.colorOverride ?? null;
        this.isPrivate_ = options?.isPrivate ?? false;
        this.showSymbol_ = options?.showSymbol ?? true;
        this.onSet_ = options?.onSet ?? null;
    }

    private createSymbol_(): SVGElement | null {
        if (!this.fieldGroup_ || !this.varType_ || !this.showSymbol_) return null;

        const cy = FIELD_HEIGHT / 2;
        const sl = this.isPrivate_ ? SYMBOL_LEFT_WITH_LOCK : SYMBOL_LEFT_NO_LOCK;
        const cx = sl + SYMBOL_SIZE / 2;

        if (this.varType_ === 'float') {
            return Blockly.utils.dom.createSvgElement(
                Blockly.utils.Svg.CIRCLE,
                { cx, cy, r: SYMBOL_SIZE / 2 + 0.5, fill: 'white', style: SVG_STYLE },
                this.fieldGroup_,
            );
        } else if (this.varType_ === 'String') {
            const h = SYMBOL_SIZE / 2;
            const points = [
                `${sl},${cy - h}`,
                `${sl + SYMBOL_SIZE},${cy - h}`,
                `${sl + SYMBOL_SIZE},${cy + h}`,
                `${sl},${cy + h}`,
            ].join(' ');
            return Blockly.utils.dom.createSvgElement(
                Blockly.utils.Svg.POLYGON,
                { points, fill: 'white', style: SVG_STYLE },
                this.fieldGroup_,
            );
        } else if (this.varType_ === 'bool') {
            const half = SYMBOL_SIZE / 2 + 2;
            const points = [
                `${cx},${cy - half}`,
                `${cx + half},${cy}`,
                `${cx},${cy + half}`,
                `${cx - half},${cy}`,
            ].join(' ');
            return Blockly.utils.dom.createSvgElement(
                Blockly.utils.Svg.POLYGON,
                { points, fill: 'white', style: SVG_STYLE },
                this.fieldGroup_,
            );
        }

        return null;
    }

    private createLock_(): SVGElement | null {
        if (!this.fieldGroup_ || !this.isPrivate_ || !this.varType_) return null;

        const lx = LOCK_LEFT;
        const cy = FIELD_HEIGHT / 2;
        const s = LOCK_SIZE;

        return Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.IMAGE,
            {
                href: '/images/block/private.svg',
                x: lx,
                y: cy - s / 2,
                width: s,
                height: s,
                style: SVG_STYLE,
            },
            this.fieldGroup_,
        );
    }

    private get textX_(): number {
        if (this.isPrivate_ && !this.showSymbol_) return LOCK_LEFT + LOCK_SIZE + LOCK_MARGIN_RIGHT;
        if (!this.varType_ || !this.showSymbol_) return TEXT_X_NO_SYMBOL;
        if (this.isPrivate_) return TEXT_X_WITH_LOCK_AND_SYMBOL;
        return TEXT_X_WITH_SYMBOL;
    }

    override initView(): void {
        this.borderRect_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.RECT,
            { rx: 6, ry: 6, x: 0, y: 0, width: FIELD_MIN_WIDTH, height: FIELD_HEIGHT },
            this.fieldGroup_,
        );

        this.lockEl_ = this.createLock_();
        this.symbolEl_ = this.createSymbol_();

        this.labelEl_ = Blockly.utils.dom.createSvgElement(
            Blockly.utils.Svg.TEXT,
            {
                x: this.textX_,
                y: FIELD_HEIGHT / 2 + 6,
                'font-size': '17px',
                'font-weight': this.fontWeight_,
                'font-family': 'Corporate Logo, sans-serif',
                style: 'fill: white !important;',
            },
            this.fieldGroup_,
        ) as SVGTextElement;

        this.updateDisplay_();
        this.applyStyle_();

        requestAnimationFrame(() => {
            this.updateDisplay_();
            (this.getSourceBlock() as Blockly.BlockSvg)?.render();
            this.fieldGroup_?.addEventListener('mouseenter', () => this.applyStyle_(true));
            this.fieldGroup_?.addEventListener('mouseleave', () => this.applyStyle_(false));
        });
    }

    private applyStyle_(hover = false): void {
        if (!this.borderRect_) return;
        const colour = this.colorOverride_ ?? this.getSourceBlock()?.getColour() ?? '#FF1BAC';
        const isPlaceholder =
            this.getValue() === '設定する変数' || this.getValue() === '変数' || /^引数\d+$/.test(this.getValue() ?? '');
        const fillColour = isPlaceholder ? darken(colour) : colour;
        this.borderRect_.setAttribute(
            'style',
            `fill: ${fillColour} !important; stroke: ${
                hover ? 'rgba(255,255,255,0.9)' : 'rgba(0,0,0,0.3)'
            }; stroke-width: ${hover ? 2 : 1};`,
        );
    }

    private calcWidth_(): number {
        const text = this.getValue() ?? '';
        let w = 0;
        for (const char of text) {
            w += char.charCodeAt(0) > 127 ? 14 : 8;
        }
        const leftPad = this.textX_;
        const isPlaceholder = text === '設定する変数' || /^引数\d+$/.test(text);
        return Math.max(isPlaceholder ? FIELD_MIN_WIDTH : 24, leftPad + w + PADDING_H);
    }

    private updateDisplay_(): void {
        if (!this.fieldGroup_ || !this.labelEl_) return;
        this.labelEl_.textContent = this.getValue() ?? '';
        this.labelEl_.setAttribute('x', String(this.textX_));
        const width = this.calcWidth_();
        this.borderRect_?.setAttribute('width', String(width));
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);

        requestAnimationFrame(() => {
            if (!this.labelEl_ || !this.borderRect_) return;
            try {
                const bbox = this.labelEl_.getBBox();
                if (bbox.width > 0) {
                    const text = this.getValue() ?? '';
                    const leftPad = this.textX_;
                    const isPlaceholder = text === '設定する変数' || /^引数\d+$/.test(text);
                    const realWidth = Math.max(isPlaceholder ? FIELD_MIN_WIDTH : 24, leftPad + bbox.width + PADDING_H);
                    this.borderRect_.setAttribute('width', String(realWidth));
                    this.size_ = new Blockly.utils.Size(realWidth, FIELD_HEIGHT);
                    (this.getSourceBlock() as Blockly.BlockSvg)?.render();
                }
            } catch {}
        });

        this.applyStyle_();
    }

    public setIsPrivate(value: boolean): void {
        if (this.isPrivate_ === value) return;
        this.isPrivate_ = value;
        if (this.lockEl_) {
            this.lockEl_.remove();
            this.lockEl_ = null;
        }
        if (this.symbolEl_) {
            this.symbolEl_.remove();
            this.symbolEl_ = null;
        }
        this.lockEl_ = this.createLock_();
        this.symbolEl_ = this.createSymbol_();
        this.labelEl_?.setAttribute('x', String(this.textX_));
        this.updateDisplay_();
        (this.getSourceBlock() as Blockly.BlockSvg)?.render();
    }

    public setVarName(name: string): void {
        if (this.onSet_ && !this.onSet_(name, this)) return;
        this.setValue(name);
        const block = this.getSourceBlock();
        if (block) {
            Blockly.Events.fire(new Blockly.Events.BlockChange(block, 'field', this.name ?? '', '', name));
        }
    }

    override doValueUpdate_(newValue: string): void {
        super.doValueUpdate_(newValue);
        if (this.fieldGroup_) this.updateDisplay_();
    }

    override getSize(): Blockly.utils.Size {
        if (this.borderRect_) {
            const w = parseFloat(this.borderRect_.getAttribute('width') ?? '0');
            if (w > 0) {
                this.size_ = new Blockly.utils.Size(w, FIELD_HEIGHT);
                return this.size_;
            }
        }
        const width = this.calcWidth_();
        this.size_ = new Blockly.utils.Size(width, FIELD_HEIGHT);
        return this.size_;
    }

    override showEditor_(): void {
        const ws = this.getSourceBlock()?.workspace as Blockly.WorkspaceSvg | undefined;
        if (ws) useBlockModalVariableStore.getState().setWorkspace(ws);
        useBlockModalVariableStore.getState().setFieldRef(this);
        useBlockModalVariableStore.getState().setFilterType(this.varType_);
        useBlockModalStore.getState().openModal('variable');
    }
}
